import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEditor } from "./useEditorState";

const SRC = { logo: "dal logo del cliente", monogram: "monogramma con nome e colori del sito", upload: "icona caricata" };

// Site favicon: automatic (compact logo, else monogram) with live preview; replace with an upload or custom letters/colour.
export default function FaviconSetting() {
  const ed = useEditor();
  const api = useApi();
  const fav = ed.content.favicon || {};
  const colors = ed.content.design?.colors || {};
  const brand = ed.content.nav?.brand || ed.project.company_name || ed.project.name || "";
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const key = JSON.stringify([fav, colors.primary, brand]);
  useEffect(() => {
    const t = setTimeout(() => api.post(`/projects/${ed.project.id}/favicon/preview`, { favicon: fav, colors, brand }).then(({ data }) => setPv(data)).catch(() => setPv(null)), 350);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", "favicon");
      const { data } = await api.post(`/projects/${ed.project.id}/assets`, fd);
      ed.setFavicon({ ...fav, asset_id: data.id, mode: "upload" }); toast.success("Icona caricata: salva per usarla nel sito");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (file.current) file.current.value = ""; }
  };
  return (
    <div className="space-y-2" data-testid="favicon-setting">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Icona del sito (favicon)</Label>
      <div className="flex items-end gap-3" data-testid="favicon-preview">
        {pv ? <>
          <img src={pv.png32} alt="Favicon 16 px" width={16} height={16} data-testid="favicon-preview-16" />
          <img src={pv.png32} alt="Favicon 32 px" width={32} height={32} data-testid="favicon-preview-32" />
          <img src={pv.png180} alt="Icona Apple 180 px" width={56} height={56} className="rounded-xl border border-slate-200" data-testid="favicon-preview-180" />
        </> : <span className="text-[11px] text-slate-400">Anteprima…</span>}
      </div>
      {pv && <p className="text-[11px] text-slate-500" data-testid="favicon-source">Attuale: {SRC[pv.source]}{pv.source === "monogram" && fav.mode !== "monogram" && !fav.letters ? " (il logo manca o è troppo largo per un'icona leggibile)" : ""}.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={fav.letters || ""} maxLength={2} placeholder={pv?.letters || "AB"} onChange={(e) => ed.setFavicon({ ...fav, letters: e.target.value.toUpperCase(), mode: "monogram", asset_id: undefined })} className="h-8 w-14 shrink-0 text-sm" aria-label="Lettere del monogramma" data-testid="favicon-letters" />
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(fav.bg || colors.primary || "") ? (fav.bg || colors.primary) : "#1f2937"} onChange={(e) => ed.setFavicon({ ...fav, bg: e.target.value })} className="h-8 w-9 shrink-0 cursor-pointer rounded border p-0.5" aria-label="Colore di sfondo dell'icona" data-testid="favicon-bg" />
        <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="favicon-file" />
        <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={() => file.current?.click()} data-testid="favicon-upload"><Upload size={13} className="mr-1" />Carica</Button>
        {(fav.asset_id || fav.letters || fav.bg || fav.mode) && <Button variant="ghost" size="sm" className="h-8" onClick={() => ed.setFavicon({})} title="Torna all'icona automatica" data-testid="favicon-reset"><RotateCcw size={13} /></Button>}
      </div>
      <p className="text-[11px] text-slate-500">Generata in automatico senza crediti AI; entra nel sito alla prossima compilazione (anteprima e online). Caricala quadrata, almeno 180×180 px.</p>
    </div>);
}
